import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Settings as SettingsIcon, User, Bell, Shield, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

// TODO: Add notification preference columns to the publishers table
// (email_new_backer, email_campaign_milestone, email_payout_processed, email_content_approved)
// and remove the localStorage fallback below.
const NOTIF_KEY = 'publisher_notification_prefs';

const loadNotifPrefs = () => {
  try {
    return JSON.parse(localStorage.getItem(NOTIF_KEY) || '{}');
  } catch {
    return {};
  }
};

const Settings = () => {
  const { publisher, refreshPublisher } = useAuth();

  // Profile form
  const [profileForm, setProfileForm] = useState({
    company_name: '',
    contact_email: '',
    brand_description: '',
  });
  const [profileOpen, setProfileOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [savingPassword, setSavingPassword] = useState(false);

  // Notification prefs (localStorage until DB columns are added)
  const [notifPrefs, setNotifPrefs] = useState({
    emailNewBacker: true,
    emailCampaignMilestone: true,
    emailPayoutProcessed: true,
    emailContentApproved: true,
    ...loadNotifPrefs(),
  });

  // Preferences
  const [darkMode, setDarkMode] = useState(true);

  // Populate profile form from context
  useEffect(() => {
    if (publisher) {
      setProfileForm({
        company_name: publisher.company_name || '',
        contact_email: publisher.contact_email || '',
        brand_description: publisher.brand_description || '',
      });
    }
  }, [publisher]);

  // Persist notification prefs to localStorage on change
  useEffect(() => {
    localStorage.setItem(NOTIF_KEY, JSON.stringify(notifPrefs));
  }, [notifPrefs]);

  const handleSaveProfile = async () => {
    if (!publisher) return;
    setSavingProfile(true);
    try {
      const { error } = await supabase
        .from('publishers')
        .update({
          company_name: profileForm.company_name,
          contact_email: profileForm.contact_email,
          brand_description: profileForm.brand_description,
        })
        .eq('id', publisher.id);

      if (error) throw error;

      await refreshPublisher();
      setProfileOpen(false);
      toast.success('Profile settings saved');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save profile settings');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSavingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordForm.newPassword,
      });
      if (error) throw error;
      setPasswordForm({ newPassword: '', confirmPassword: '' });
      toast.success('Password updated successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  const toggleNotif = (key: keyof typeof notifPrefs) => {
    setNotifPrefs((prev: typeof notifPrefs) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className='p-4 md:p-6 space-y-4 md:space-y-6 text-foreground'>
      <div>
        <h1 className='text-2xl md:text-3xl font-bold'>Settings</h1>
        <p className='text-muted-foreground text-sm md:text-base'>
          Manage your account and preferences
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6'>

        {/* Profile Settings */}
        <Card className='p-4 md:p-6 partner-card'>
          <div className='flex items-center space-x-3 md:space-x-4 mb-4'>
            <User className='h-5 w-5 md:h-6 md:w-6 text-primary flex-shrink-0' />
            <h3 className='text-base md:text-lg font-semibold'>Profile Settings</h3>
          </div>

          {!profileOpen ? (
            <>
              <div className='space-y-2 mb-4'>
                <p className='text-sm text-muted-foreground'>
                  <span className='text-foreground font-medium'>Company:</span>{' '}
                  {publisher?.company_name || '—'}
                </p>
                <p className='text-sm text-muted-foreground'>
                  <span className='text-foreground font-medium'>Email:</span>{' '}
                  {publisher?.contact_email || '—'}
                </p>
                {publisher?.brand_description && (
                  <p className='text-sm text-muted-foreground line-clamp-2'>
                    {publisher.brand_description}
                  </p>
                )}
              </div>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setProfileOpen(true)}
                className='border-border/60'
              >
                Edit Profile
              </Button>
            </>
          ) : (
            <div className='space-y-3'>
              <div className='space-y-1.5'>
                <Label htmlFor='company_name'>Company Name</Label>
                <Input
                  id='company_name'
                  value={profileForm.company_name}
                  onChange={e => setProfileForm(p => ({ ...p, company_name: e.target.value }))}
                  disabled={savingProfile}
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='contact_email'>Contact Email</Label>
                <Input
                  id='contact_email'
                  type='email'
                  value={profileForm.contact_email}
                  onChange={e => setProfileForm(p => ({ ...p, contact_email: e.target.value }))}
                  disabled={savingProfile}
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='brand_description'>Brand Description</Label>
                <Input
                  id='brand_description'
                  value={profileForm.brand_description}
                  onChange={e => setProfileForm(p => ({ ...p, brand_description: e.target.value }))}
                  placeholder='Short public description of your studio'
                  disabled={savingProfile}
                />
              </div>
              <div className='flex gap-2 pt-1'>
                <Button size='sm' onClick={handleSaveProfile} disabled={savingProfile}>
                  {savingProfile && <Loader2 className='h-3 w-3 mr-1.5 animate-spin' />}
                  Save
                </Button>
                <Button
                  size='sm'
                  variant='ghost'
                  onClick={() => setProfileOpen(false)}
                  disabled={savingProfile}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Notifications */}
        <Card className='p-4 md:p-6 partner-card'>
          <div className='flex items-center space-x-3 md:space-x-4 mb-4'>
            <Bell className='h-5 w-5 md:h-6 md:w-6 text-publisher-300 flex-shrink-0' />
            <h3 className='text-base md:text-lg font-semibold'>Notifications</h3>
          </div>
          <div className='space-y-3'>
            {([
              { key: 'emailNewBacker', label: 'New backer on campaign' },
              { key: 'emailCampaignMilestone', label: 'Campaign milestone reached' },
              { key: 'emailPayoutProcessed', label: 'Payout processed' },
              { key: 'emailContentApproved', label: 'Content approved' },
            ] as const).map(({ key, label }) => (
              <div key={key} className='flex items-center justify-between'>
                <Label htmlFor={key} className='text-sm cursor-pointer'>{label}</Label>
                <Switch
                  id={key}
                  checked={notifPrefs[key]}
                  onCheckedChange={() => toggleNotif(key)}
                />
              </div>
            ))}
          </div>
        </Card>

        {/* Security */}
        <Card className='p-4 md:p-6 partner-card'>
          <div className='flex items-center space-x-3 md:space-x-4 mb-4'>
            <Shield className='h-5 w-5 md:h-6 md:w-6 text-theatre-300 flex-shrink-0' />
            <h3 className='text-base md:text-lg font-semibold'>Security</h3>
          </div>
          <div className='space-y-3'>
            <div className='space-y-1.5'>
              <Label htmlFor='newPassword'>New Password</Label>
              <Input
                id='newPassword'
                type='password'
                placeholder='Enter new password'
                value={passwordForm.newPassword}
                onChange={e => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))}
                disabled={savingPassword}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='confirmPassword'>Confirm Password</Label>
              <Input
                id='confirmPassword'
                type='password'
                placeholder='Confirm new password'
                value={passwordForm.confirmPassword}
                onChange={e => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))}
                disabled={savingPassword}
              />
            </div>
            <Button
              size='sm'
              onClick={handleChangePassword}
              disabled={savingPassword || !passwordForm.newPassword}
              className='mt-1'
            >
              {savingPassword && <Loader2 className='h-3 w-3 mr-1.5 animate-spin' />}
              Update Password
            </Button>
          </div>
        </Card>

        {/* Preferences */}
        <Card className='p-4 md:p-6 partner-card'>
          <div className='flex items-center space-x-3 md:space-x-4 mb-4'>
            <SettingsIcon className='h-5 w-5 md:h-6 md:w-6 text-business-300 flex-shrink-0' />
            <h3 className='text-base md:text-lg font-semibold'>Preferences</h3>
          </div>
          <div className='space-y-3'>
            <div className='flex items-center justify-between'>
              <div>
                <Label htmlFor='darkMode' className='text-sm cursor-pointer'>Dark Mode</Label>
                <p className='text-xs text-muted-foreground mt-0.5'>
                  The portal always uses dark mode by default
                </p>
              </div>
              <Switch
                id='darkMode'
                checked={darkMode}
                onCheckedChange={setDarkMode}
              />
            </div>
            <Separator />
            <p className='text-xs text-muted-foreground'>
              Additional display preferences will be available in a future update.
            </p>
          </div>
        </Card>

      </div>
    </div>
  );
};

export default Settings;
